import { RefObject, useCallback, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { useFeature } from 'src/app/FeatureToggle';
import { useAppSelector } from 'src/app/store/hooks';
import {
  useTripsSubscriptionsCreateMutation,
  useTripsSubscriptionsPartialUpdateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { IToastConfig } from 'src/common/primitives/Toast';
import { show } from 'src/common/primitives/Toast/toast.slice';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { setShowTripSubscriptionModal } from '../trip-subscriptions.slice';
import { assignDefaultFormValues } from './assignDefaultFormValues';
import { formatInvoiceValidationError } from './formatInvoiceValidationError';
import { TripSubscriptionFormValues } from './FormValues';
import generateTripSubscriptionFromFormValues from './generateTripSubscriptionFromFormValues';
import validateFormData from './validateFormData';

const generateCreateSuccessToast = (id: number): IToastConfig => {
  return {
    id,
    title: 'Subscription created',
    description: 'The subscription has been created',
    type: 'success',
  };
};

const generateUpdateSuccessToast = (id: number): IToastConfig => {
  return {
    id,
    title: 'Subscription updated',
    description: 'The subscription has been updated',
    type: 'success',
  };
};

export function useFormManagement(
  setError: (error: string | null) => void,
  errorRef: RefObject<HTMLDivElement>
) {
  const dispatch = useDispatch();
  const selectedTripSubscription = useAppSelector(
    (state) => state.tripSubscription.selectedTripSubscription
  );

  const selectedPayer = useAppSelector(
    (state) => state.tripSubscription.selectedPayer
  );

  const selectedPassenger = useAppSelector(
    (state) => state.tripSubscription.selectedPassenger
  );

  const nextToastId = useAppSelector((state) => state.toast.nextId);

  const { isEnabled: isTripRequiredFieldsEnabled } = useFeature(
    'trip required fields'
  );

  const defaultValues = assignDefaultFormValues(selectedTripSubscription);
  const methods = useForm({ defaultValues });
  const [formattedError, setFormattedError] = useState<Record<
    string,
    string
  > | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createSubscription] = useTripsSubscriptionsCreateMutation();
  const [updateSubscription] = useTripsSubscriptionsPartialUpdateMutation();

  const onFormSubmit = useCallback(
    async (values: TripSubscriptionFormValues) => {
      setIsSubmitting(true);
      try {
        setError(null);
        setFormattedError(null);
        const errors = validateFormData(
          values,
          isTripRequiredFieldsEnabled,
          selectedPayer,
          selectedPassenger
        );

        if (errors.length > 0) {
          // TODO: handle multiple errors
          // will follow up with another PR
          setError(errors[0]);
          setIsSubmitting(false);
          errorRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
          return;
        }

        if (values.id) {
          await updateSubscription({
            id: values.id,
            patchedTripSubscription:
              generateTripSubscriptionFromFormValues(values),
          }).unwrap();
          dispatch(show(generateUpdateSuccessToast(nextToastId)));
        } else {
          await createSubscription({
            tripSubscription: generateTripSubscriptionFromFormValues(values),
          }).unwrap();
          dispatch(show(generateCreateSuccessToast(nextToastId)));
        }

        dispatch(setShowTripSubscriptionModal(false));
      } catch (error) {
        const invoiceError = formatInvoiceValidationError(error);
        if (invoiceError) {
          setFormattedError(invoiceError);
        } else {
          setError(formatServerError(error));
        }
        errorRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      setError,
      isTripRequiredFieldsEnabled,
      selectedPayer,
      selectedPassenger,
      dispatch,
      errorRef,
      updateSubscription,
      nextToastId,
      createSubscription,
    ]
  );

  return { methods, isSubmitting, onFormSubmit, formattedError };
}
